'use client';

import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { migrateLegacyThemeFromLocalStorage, readThemeCookie, writeThemeCookie } from '@/platform/cookies/consent';
import {
  applyTheme,
  resolveEffectiveTheme,
  THEME_BG_OPTIONS,
  THEME_COLOR_SWATCHES,
  THEME_DEFAULT,
  THEME_FONT_SIZES,
  type ThemeState,
} from '@/platform/theme/themeBootstrap';
import { useSiteAppearance } from '@/platform/siteappearance/SiteAppearanceProvider';

export type { ThemeState };
export { applyTheme, THEME_DEFAULT };

export function ThemeSettingsProvider({ children }: { children: React.ReactNode }) {
  const { appearance } = useSiteAppearance();

  useLayoutEffect(() => {
    migrateLegacyThemeFromLocalStorage();
    applyTheme(resolveEffectiveTheme(readThemeCookie(), appearance));
  }, [appearance]);

  return <>{children}</>;
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ThemeSettingsModal({ open, onClose }: Props) {
  const { appearance } = useSiteAppearance();
  const [state, setState] = useState<ThemeState>(THEME_DEFAULT);
  const accentLocked = appearance.accentLocked;

  useLayoutEffect(() => {
    if (open) {
      setState(resolveEffectiveTheme(readThemeCookie(), appearance));
    }
  }, [open, appearance]);

  function save(next: ThemeState) {
    const merged = accentLocked
      ? { ...next, primaryHue: appearance.lockedPrimaryHue ?? THEME_DEFAULT.primaryHue }
      : next;
    setState(merged);
    applyTheme(resolveEffectiveTheme(JSON.stringify(merged), appearance));
    writeThemeCookie(JSON.stringify(merged));
  }

  if (!open) return null;

  return createPortal(
    <div
      className="theme-modal theme-modal--open"
      role="dialog"
      aria-modal="true"
      aria-label="Customize your view"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="theme-modal__card">
        <h2>Customize your view</h2>
        <p className="text-muted">Font size, accent color, and background.</p>

        <div className="theme-modal__section">
          <h4>Font size</h4>
          <div className="theme-modal__sizes">
            {THEME_FONT_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className={`theme-modal__size${state.fontSize === size ? ' theme-modal__size--active' : ''}`}
                aria-label={`${size} font`}
                onClick={() => save({ ...state, fontSize: size })}
              />
            ))}
          </div>
        </div>

        {!accentLocked && (
          <div className="theme-modal__section">
            <h4>Color</h4>
            <div className="theme-modal__colors">
              {THEME_COLOR_SWATCHES.map((c) => (
                <button
                  key={c.hue}
                  type="button"
                  className={`theme-modal__color${state.primaryHue === c.hue ? ' theme-modal__color--active' : ''}`}
                  style={{ background: `hsl(${c.hue}, 75%, 60%)` }}
                  aria-label={`Hue ${c.hue}`}
                  onClick={() => save({ ...state, primaryHue: c.hue })}
                />
              ))}
            </div>
          </div>
        )}

        <div className="theme-modal__section">
          <h4>Background</h4>
          <div className="theme-modal__backgrounds">
            {THEME_BG_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`theme-modal__bg theme-modal__bg--${opt.cssClass}${state.bg === opt.id ? ' theme-modal__bg--active' : ''}`}
                onClick={() => save({ ...state, bg: opt.id })}
              >
                <span />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button type="button" className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={onClose}>
          Done
        </button>
      </div>
    </div>,
    document.body,
  );
}
